/**
 * Serviço de comunicação com o Mercado Pago no frontend
 * 
 * Este serviço encapsula as chamadas à API do backend para
 * operações relacionadas ao Mercado Pago.
 */

import type { AxiosError } from 'axios';
import api from './api';
import type { PaymentProviderRoutesDto } from '../types/checkoutPayment';

function checkoutErrorMessage(err: unknown): string {
  const ax = err as AxiosError<{ message?: string }>;
  const data = ax.response?.data;
  const fromBody = typeof data?.message === 'string' ? data.message.trim() : '';
  if (fromBody) return fromBody;
  if (typeof ax.message === 'string' && ax.message) return ax.message;
  return 'Falha ao processar pagamento. Confira os logs do servidor ou o retorno da API.';
}
/**
 * Interface para itens de pagamento
 */
export interface PaymentItem {
  id?: string;
  title: string;
  description?: string;
  picture_url?: string;
  category_id?: string;
  quantity: number;
  unit_price: number;
  currency_id?: string;
}

/**
 * Interface para dados do pagador
 */
export interface Payer {
  name?: string;
  surname?: string;
  email: string;
  phone?: {
    area_code?: string;
    number?: string;
  };
  identification?: {
    type?: string;
    number?: string;
  };
  address?: {
    street_name?: string;
    street_number?: number;
    zip_code?: string;
  };
}

/**
 * Interface para criação de preferência de pagamento
 */
export interface CreatePaymentPreferenceOptions {
  items: PaymentItem[];
  payer?: Payer;
  external_reference?: string;
  statement_descriptor?: string;
}

export interface ProcessPaymentData {
  ticketId?: string;
  productId?: string;
  eventId: string;
  quantity: number;
  transactionAmount: number;
  /** Opcional em Pix (Pagar.me ou MP). */
  cardToken?: string;
  installments: number;
  paymentMethodId: string;
  issuerId?: string;
  customerEmail: string;
  customerName: string;
  customerCPF: string;
  customerPhone?: string;
  customFields?: Record<string, any>;
  external_reference?: string;
  description?: string;
}

export interface InstallmentSettings {
  enabled: boolean;
  maxInstallments: number;
}

/**
 * Cria uma preferência de pagamento
 * 
 * @param options Opções para criação da preferência
 * @returns Dados da preferência criada
 */


export const createPaymentPreference = async (options: CreatePaymentPreferenceOptions) => {
  try {
    const response = await api.post(`/payments`, options);
    return response.data.data;
  } catch (error) {
    console.error('Erro ao criar preferência de pagamento:', error);
    throw new Error('Falha ao criar preferência de pagamento');
  }
};

export const processPayment = async (paymentData: ProcessPaymentData) => {
  try {
    const response = await api.post('/payments/process-payment', paymentData);
    return response.data;
  } catch (error) {
    console.error('Erro ao processar pagamento:', error);
    throw new Error(checkoutErrorMessage(error));
  }
};

export const getInstallmentSettings = async (): Promise<InstallmentSettings> => {
  const response = await api.get('/payments/installment-settings');
  return response.data?.data || { enabled: true, maxInstallments: 12 };
};

const DEFAULT_PROVIDER_ROUTES: PaymentProviderRoutesDto = {
  pix: 'pagarme',
  credit_card: 'mercadopago',
  debit_card: 'mercadopago',
};

export const getPaymentProviderRoutes = async (opts?: {
  eventId?: string;
  registrationId?: string;
}): Promise<PaymentProviderRoutesDto> => {
  try {
    const response = await api.get('/payments/provider-routes', {
      params: {
        ...(opts?.eventId ? { eventId: opts.eventId } : {}),
        ...(opts?.registrationId ? { registrationId: opts.registrationId } : {}),
      },
    });
    const d = response.data?.data;
    if (!d) {
      return { ...DEFAULT_PROVIDER_ROUTES };
    }
    return {
      pix: d.pix === 'pagarme' ? 'pagarme' : 'mercadopago',
      credit_card: d.credit_card === 'pagarme' ? 'pagarme' : 'mercadopago',
      debit_card: d.debit_card === 'pagarme' ? 'pagarme' : 'mercadopago',
    };
  } catch {
    return { ...DEFAULT_PROVIDER_ROUTES };
  }
};


/**
 * Obtém informações de um pagamento
 * 
 * @param paymentId ID do pagamento
 * @returns Dados do pagamento
 */
export const getPayment = async (paymentId: string) => {
  try {
    const response = await api.get(`/payments/${paymentId}`);
    return response.data.data;
  } catch (error) {
    console.error(`Erro ao buscar pagamento ${paymentId}:`, error);
    throw new Error(`Falha ao buscar informações do pagamento ${paymentId}`);
  }
};

export const generatePaymentByRegistration = async (registrationId: string, paymentData: any) => {
  try {
    const response = await api.post(
      `/payments/process-payment/${registrationId}`, 
      paymentData // 🔥 Envia os dados no corpo
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar pagamento:', error);
    throw new Error('Falha ao gerar pagamento');
  }
};


const mercadoPagoService = {
  createPaymentPreference,
  getPayment,
  processPayment,
  getInstallmentSettings,
  getPaymentProviderRoutes,
  generatePaymentByRegistration,
};

export default mercadoPagoService;
